import type {
  SimilarRequest,
  SimilarResponse,
  PostsPerHourData,
  TopLanguagesData,
  TopDomainsData,
  ContentStatsData,
  CustomQueryRequest,
  CustomQueryResponse,
  AnalyticsStatus,
  ServiceMetrics,
  Post,
} from '@/types';

let frozenPostsCache: Post[] | null = null;

async function fetchFrozen<T>(path: string): Promise<T> {
  const res = await fetch(path, {
    cache: 'force-cache',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!res.ok) {
    throw new Error(`Frozen snapshot load failed: ${res.status}`);
  }

  return res.json() as Promise<T>;
}

async function getFrozenPosts(): Promise<Post[]> {
  if (frozenPostsCache) {
    return frozenPostsCache;
  }
  const payload = await fetchFrozen<{ posts: Post[] }>('/frozen/search-posts.json');
  frozenPostsCache = payload.posts || [];
  return frozenPostsCache;
}

function matchesFilters(post: Post, filters: SimilarRequest['filters']): boolean {
  if (!filters) return true;

  if (filters.has_link !== undefined && post.has_link !== filters.has_link) {
    return false;
  }
  if (filters.has_media !== undefined && post.has_media !== filters.has_media) {
    return false;
  }
  if (filters.langs && filters.langs.length > 0) {
    const langs = post.langs || [];
    if (!filters.langs.some((lang) => langs.includes(lang))) {
      return false;
    }
  }
  if (filters.min_length && post.text.length < filters.min_length) {
    return false;
  }
  if (filters.time_range?.start) {
    const postTime = new Date(post.created_at).getTime();
    const startTime = new Date(filters.time_range.start).getTime();
    if (!Number.isNaN(startTime) && postTime < startTime) {
      return false;
    }
  }
  if (filters.time_range?.end) {
    const postTime = new Date(post.created_at).getTime();
    const endTime = new Date(filters.time_range.end).getTime();
    if (!Number.isNaN(endTime) && postTime > endTime) {
      return false;
    }
  }

  return true;
}

function scoreKeyword(post: Post, query: string): number {
  const q = query.toLowerCase();
  const t = post.text.toLowerCase();
  if (!q.trim()) return 0;
  if (!t.includes(q)) return 0;

  const occurrences = t.split(q).length - 1;
  const recencyBoost = new Date(post.created_at).getTime() / 1e13;
  return occurrences * 10 + recencyBoost;
}

function scoreSemantic(post: Post, query: string): number {
  const terms = query
    .toLowerCase()
    .split(/\s+/)
    .map((term) => term.trim())
    .filter(Boolean);
  if (terms.length === 0) return 0;

  const text = post.text.toLowerCase();
  let matches = 0;
  for (const term of terms) {
    if (text.includes(term)) matches += 1;
  }

  const matchRatio = matches / terms.length;
  const recencyBoost = new Date(post.created_at).getTime() / 1e13;
  return matchRatio * 100 + recencyBoost;
}

async function searchFrozen(request: SimilarRequest, semantic: boolean): Promise<SimilarResponse> {
  const posts = await getFrozenPosts();
  const limit = request.limit && request.limit > 0 ? request.limit : 20;

  const scored = posts
    .filter((post) => matchesFilters(post, request.filters))
    .map((post) => ({
      post,
      score: semantic ? scoreSemantic(post, request.text) : scoreKeyword(post, request.text),
    }))
    .filter((item) => item.score > 0)
    .sort((a, b) => b.score - a.score);

  const selected = scored.slice(0, limit).map((item) => ({ ...item.post, score: item.score }));

  if (request.prefer_recent) {
    selected.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
  }

  return {
    posts: selected,
    total: selected.length,
  };
}

export async function searchSimilar(request: SimilarRequest): Promise<SimilarResponse> {
  return searchFrozen(request, true);
}

export async function searchBM25(request: SimilarRequest): Promise<SimilarResponse> {
  return searchFrozen(request, false);
}

export async function getPostsPerHour(): Promise<PostsPerHourData[]> {
  const response = await fetchFrozen<{ data: PostsPerHourData[] }>('/frozen/posts-per-hour.json');
  return response.data || [];
}

export async function getTopLanguages(): Promise<TopLanguagesData[]> {
  const response = await fetchFrozen<{ data: Array<{ lang: string; count: number }> }>('/frozen/top-languages.json');
  const data = response.data || [];
  const total = data.reduce((sum, row) => sum + row.count, 0);
  return data.map((row) => ({
    language: row.lang,
    count: row.count,
    percentage: total > 0 ? (row.count / total) * 100 : 0,
  }));
}

export async function getTopDomains(): Promise<TopDomainsData[]> {
  const response = await fetchFrozen<{ data: TopDomainsData[] }>('/frozen/top-domains.json');
  return response.data || [];
}

export async function getContentStats(): Promise<ContentStatsData | null> {
  const response = await fetchFrozen<{ data: ContentStatsData }>('/frozen/content-stats.json');
  return response.data || null;
}

export async function runCustomQuery(_request: CustomQueryRequest): Promise<CustomQueryResponse> {
  throw new Error('Custom SQL is disabled in frozen snapshot mode');
}

export async function getAnalyticsStatus(): Promise<AnalyticsStatus> {
  return fetchFrozen<AnalyticsStatus>('/frozen/analytics-status.json');
}

export async function getFrozenFirehosePosts(): Promise<Post[]> {
  const payload = await fetchFrozen<{ posts: Post[] }>('/frozen/firehose-posts.json');
  return payload.posts || [];
}

export function getFirehoseWebSocketUrl(): string {
  return '';
}

export async function getServiceMetrics(): Promise<ServiceMetrics> {
  return fetchFrozen<ServiceMetrics>('/frozen/service-metrics.json');
}
